import { useCallback, useEffect, useState } from "react";
import { Loader2, PhoneCall, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import config from "@/config";
import { cn } from "@/utils";

type ActiveCallRow = {
  call_id: string;
  status: string;
  age_minutes: number | null;
  stale: boolean;
};

type ActiveCallsResponse = {
  active: number;
  live: number;
  stale: number;
  stale_after_minutes: number;
  calls: ActiveCallRow[];
  cleared?: number;
  failed?: number;
  requested?: number;
};

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown; error?: unknown };
    if (typeof data.detail === "string" && data.detail) return data.detail;
    if (typeof data.error === "string" && data.error) return data.error;
  } catch {
    // body wasn't JSON
  }
  return fallback;
}

function ageLabel(minutes: number | null): string {
  if (minutes == null) return "unknown age";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours}h ${rest}m` : `${hours}h`;
}

export function ActiveCallsControl() {
  const [data, setData] = useState<ActiveCallsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/voice/active-calls`,
      );
      if (!res.ok) {
        throw new Error(await readError(res, `active calls failed: ${res.status}`));
      }
      setData((await res.json()) as ActiveCallsResponse);
    } catch (err) {
      setError(err instanceof Error ? err.message : "active calls failed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const clearStale = useCallback(async () => {
    if (!data || data.stale < 1 || clearing) return;
    const minutes = data.stale_after_minutes;
    const ok = window.confirm(
      `Clear ${data.stale} stale call${data.stale === 1 ? "" : "s"}? Calls open longer than ${minutes} minutes are hung up and their slots are freed. Newer calls stay connected.`,
    );
    if (!ok) return;
    setClearing(true);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/api/dashboard/voice/active-calls/clear-stale`,
        { method: "POST" },
      );
      if (!res.ok) {
        throw new Error(await readError(res, `clear stale failed: ${res.status}`));
      }
      const body = (await res.json()) as ActiveCallsResponse;
      setData(body);
      setOpen(true);
      const failed = body.failed ?? 0;
      setNotice(
        failed
          ? `Cleared ${body.cleared ?? 0}. ${failed} could not be cleared.`
          : `Cleared ${body.cleared ?? 0} stale call${body.cleared === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "clear stale failed");
    } finally {
      setClearing(false);
    }
  }, [clearing, data]);

  const active = data?.active ?? 0;
  const staleRows = (data?.calls ?? []).filter((row) => row.stale);
  const threshold = data?.stale_after_minutes ?? 30;

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          <PhoneCall className="h-4 w-4" />
          {loading && !data ? "Active calls" : `${active} active`}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          onClick={() => void refresh()}
          disabled={loading || clearing}
          aria-label="Refresh active calls"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={() => void clearStale()}
          disabled={!data || data.stale < 1 || clearing || loading}
        >
          {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Clear stale{data && data.stale > 0 ? ` (${data.stale})` : ""}
        </Button>
      </div>
      {data ? (
        <p className="text-xs text-muted-foreground">
          {data.live} live · {data.stale} stale after {threshold} min
        </p>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
      {notice ? <p className="text-xs text-muted-foreground">{notice}</p> : null}
      {open && data ? (
        <div className="w-80 rounded-md border bg-background p-3 text-left shadow-sm">
          {staleRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No stale calls. {data.live} live call{data.live === 1 ? "" : "s"} under{" "}
              {threshold} minutes.
            </p>
          ) : (
            <ul className="max-h-52 space-y-2 overflow-auto">
              {staleRows.map((row) => (
                <li
                  key={row.call_id || `${row.status}-${row.age_minutes}`}
                  className="flex items-center justify-between gap-3 text-sm"
                >
                  <span className={cn("font-mono text-xs", !row.call_id && "text-muted-foreground")}>
                    {row.call_id ? row.call_id.slice(-8) : "no id"}
                  </span>
                  <span className="text-muted-foreground">
                    {row.status} · {ageLabel(row.age_minutes)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
